import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUser, FaMedal, FaTimes } from "react-icons/fa";
import { BiUserCircle } from "react-icons/bi";
import { MdDashboard } from "react-icons/md";
import { BsFileEarmarkCheckFill } from "react-icons/bs";
import { RxDashboard } from "react-icons/rx";
import { FaAngleUp, FaAngleDown } from "react-icons/fa6";
import { GiProgression } from "react-icons/gi";
import { BiVideo } from "react-icons/bi";
import { MdOutlineDocumentScanner } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getOrganizationById } from "../../store/reducers/organizationSlice";
import { FaRegQuestionCircle } from "react-icons/fa";

const UserMenuSidebar = ({ toggleMenuSidebar }) => {
  const [t] = useTranslation();
  const [badgesOpen, setBadgesOpen] = useState(false);
  const dispatch = useDispatch();
  const { customPoints } = useSelector((state) => state.badgesDetails);
  const { organizationDetails, selectedOrganization } = useSelector(
    (state) => state.organization,
  );

  const allAccess =
    !customPoints || !Array.isArray(customPoints.reward_permission);

  const canAccessScoreboard =
    allAccess || customPoints.reward_permission.includes("scoreboard");
  const canAccessLeaderboard =
    allAccess || customPoints.reward_permission.includes("leader_board");
  const canAccessEmployeePerformance =
    allAccess ||
    customPoints.reward_permission.includes("employee_performance");
  const canAccessBadges =
    allAccess || customPoints.reward_permission.includes("badges");

  const { organizations } = useSelector((state) => state.userOrganizations);
  const isAdmin_Owner = organizations.some(
    (org) =>
      org.organisation_id === +localStorage.getItem("organisation_id") &&
      (org.role === "Admin" || org.role === "Owner"),
  );

  const role = localStorage.getItem("user_role");
  const isOwner_Admin = role === "Admin" || role === "Owner";
  const activeOrganisationId =
    selectedOrganization?.organisation_id ||
    localStorage.getItem("organisation_id");
  const organizationLogo = organizationDetails?.logo || "";
  const hasCustomLogo = Boolean(organizationLogo);
  const defaultSidebarLogo = "/img/Logo_negative.svg";
  const [isOrganizationLogoLoaded, setIsOrganizationLogoLoaded] =
    useState(false);
  const sidebarLogo =
    hasCustomLogo && isOrganizationLogoLoaded
      ? organizationLogo
      : defaultSidebarLogo;

  useEffect(() => {
    const detailsOrganisationId =
      organizationDetails?.id || organizationDetails?.organisation_id;

    if (
      activeOrganisationId &&
      String(detailsOrganisationId || "") !== String(activeOrganisationId)
    ) {
      dispatch(getOrganizationById(activeOrganisationId));
    }
  }, [activeOrganisationId, organizationDetails, dispatch]);

  useEffect(() => {
    const pathsToOpen = [
      "/badges_dashboard",
      "/badges",
      "/leaderboard",
      "/employee-progress",
      "/employee-progress/:id",
    ];
    const currentPath = window.location.pathname;
    const isDynamicEmployeeProgress = /^\/employee-progress\/[^/]+$/.test(
      currentPath,
    );
    if (pathsToOpen.includes(currentPath) || isDynamicEmployeeProgress) {
      setBadgesOpen(true);
    } else {
      setBadgesOpen(false);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    if (!hasCustomLogo) {
      setIsOrganizationLogoLoaded(false);
      return;
    }

    setIsOrganizationLogoLoaded(false);

    const logoImage = new Image();
    logoImage.src = organizationLogo;
    logoImage.onload = () => setIsOrganizationLogoLoaded(true);
    logoImage.onerror = () => setIsOrganizationLogoLoaded(false);

    return () => {
      logoImage.onload = null;
      logoImage.onerror = null;
    };
  }, [hasCustomLogo, organizationLogo]);

  return (
    <aside className="main-sidebar bg-bg_color bg_color elevation-5">
      <div className="sidebar">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <div className="user-sidebar mt-3 pb-3 mb-3 d-flex">
            <NavLink
              to="/"
              className="text-center"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  backgroundColor: "transparent",
                  width: "100%",
                  maxWidth: hasCustomLogo ? "220px" : "unset",
                  height: hasCustomLogo ? "72px" : "35px",
                  paddingTop: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: hasCustomLogo ? "hidden" : "visible",
                  margin: "0 auto",
                }}
              >
                <img
                  src={sidebarLogo}
                  alt="logo"
                  style={{
                    maxWidth:
                      hasCustomLogo && isOrganizationLogoLoaded
                        ? "100%"
                        : "80%",
                    maxHeight:
                      hasCustomLogo && isOrganizationLogoLoaded
                        ? "100%"
                        : "none",
                    width:
                      hasCustomLogo && isOrganizationLogoLoaded
                        ? "auto"
                        : "80%",
                    height: "auto",
                    objectFit:
                      hasCustomLogo && isOrganizationLogoLoaded
                        ? "contain"
                        : "initial",
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              </div>
            </NavLink>
          </div>

          {/* Close button - visible only on mobile */}
          <div className="d-block d-md-none text-right p-1 mt-3">
            <button
              onClick={toggleMenuSidebar}
              style={{
                background: "transparent",
                border: "none",
                fontSize: "1rem",
                cursor: "pointer",
                color: "var(--org-font-color, #fff)",
              }}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <nav className="mt-6" style={{ width: "100%" }}>
          <ul
            className="nav nav-pill nav-sidebar flex-column"
            data-widget="treeview"
            role="menu"
            data-accordion="false"
          >
            <li className="nav-item">
              <NavLink to="/dashboard" exact className="nav-link">
                <MdDashboard size={22} />
                <p className="ml-2">
                  {t("userMenuSidebar.label.AC-Analytics")}
                </p>
              </NavLink>
            </li>

        {organizationDetails?.view_educational_videos && (
              <li className="nav-item">
                <NavLink to="/educational-videos" exact className="nav-link">
                  <BiVideo size={22} />
                  <p className="ml-2">
                    {t("userMenuSidebar.label.Educational_video")}
                  </p>
                </NavLink>
              </li>
            )}

                   <li className="nav-item">
              <NavLink to="/quizzes" exact className="nav-link">
                <FaRegQuestionCircle size={22} />
                <p className="ml-2"> {t("userMenuSidebar.label.Quizzes")}</p>
              </NavLink>
            </li>

            {organizationDetails?.view_ac_online && (
              <li className="nav-item">
                <NavLink to="/view-ac-cards" exact className="nav-link">
                  <MdOutlineDocumentScanner size={22} />
                  <p className="ml-2"> {t("userMenuSidebar.label.View_AC")}</p>
                </NavLink>
              </li>
            )}

            
            <li className="nav-item">
              <div
                className="nav-link d-flex align-items-center justify-content-between"
                style={{ cursor: "pointer" }}
                onClick={() => setBadgesOpen((open) => !open)}
              >
                <span className="d-flex align-items-center">
                  <FaMedal size={22} />
                  <span>
                    <span className="ml-2">Award menu</span>
                    <span className="ml-2" style={{ fontSize: "10px" }}>
                      (BETA)
                    </span>
                  </span>
                </span>
                <span>
                  {badgesOpen ? (
                    <FaAngleUp size={18} />
                  ) : (
                    <FaAngleDown size={18} />
                  )}
                </span>
              </div>
              {badgesOpen && (
                <ul
                  className="nav nav-pill nav-sidebar flex-column"
                  data-widget="treeview"
                  role="menu"
                  data-accordion="false"
                >
                  {canAccessScoreboard && (
                    <li className="nav-item">
                      <NavLink
                        to="/badges_dashboard"
                        className="nav-link"
                        style={{ paddingLeft: "2rem" }}
                      >
                        <RxDashboard size={18} className="mr-2" /> Scoreboard
                      </NavLink>
                    </li>
                  )}
                  {(isAdmin_Owner || isOwner_Admin) &&
                    canAccessEmployeePerformance && (
                      <li>
                        <NavLink
                          to="/employee-progress"
                          className={({ isActive, location }) => {
                            const path =
                              location?.pathname || window.location.pathname;
                            const match =
                              path === "/employee-progress" ||
                              /^\/employee-progress\/(\d+|[^/]+)$/.test(path);
                            return `nav-link${match ? " active" : ""}`;
                          }}
                          style={{ paddingLeft: "2rem" }}
                        >
                          <GiProgression size={18} className="mr-2" /> Employee
                          Performance
                        </NavLink>
                      </li>
                    )}
                  {canAccessBadges && (
                    <li>
                      <NavLink
                        to="/badges"
                        className="nav-link"
                        style={{ paddingLeft: "2rem" }}
                      >
                        <FaMedal size={18} className="mr-2" /> Badges
                      </NavLink>
                    </li>
                  )}
                  {canAccessLeaderboard && (
                    <li>
                      <NavLink
                        to="/leaderboard"
                        className="nav-link"
                        style={{ paddingLeft: "2rem" }}
                      >
                        <BsFileEarmarkCheckFill size={18} className="mr-2" />{" "}
                        Leaderboard
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>

            <li className="nav-item">
              <NavLink to="/account-details" exact className="nav-link">
                <FaUser size={22} />
                <p className="ml-2">
                  {t("userMenuSidebar.label.AccountDetails")}
                </p>
              </NavLink>
            </li>   

            {localStorage.getItem("type") === "Private" ? null : (
              <li className="nav-item">
                <NavLink to="/change-org" exact className="nav-link">
                  <BiUserCircle size={22} />
                  <p className="ml-2">{t("userMenuSidebar.label.ChangeOrg")}</p>
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default UserMenuSidebar;
